import React, { FC, useState } from 'react';

type Tab = 'RECEIVE' | 'SEND' | 'BIDIRECTIONAL';

const SftpSetupGuide: FC = () => {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState<Tab>('RECEIVE');

    const tabClass = (t: Tab) =>
        `rounded px-3 py-1 text-xs font-medium ${
            tab === t ? 'bg-[#bd902e] text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
        }`;

    return (
        <div className="admin-motion-card rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700">
            <button
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 font-medium text-slate-800 hover:bg-slate-100"
                onClick={() => setOpen((v) => !v)}
            >
                SFTP setup guide (SEND / RECEIVE / BIDIRECTIONAL)
                <span className="text-xs text-slate-500">{open ? 'Hide' : 'Show'}</span>
            </button>
            {open && (
                <div className="space-y-3 border-t border-slate-200 px-3 py-3">
                    <p className="text-xs text-slate-600">
                        Antar connects as an SFTP client to partner servers for <strong>REMOTE</strong> outbound, or
                        writes to an <strong>Antar-hosted</strong> outbox when <code>deliveryMode=HOSTED</code> (see{' '}
                        <a href="/docs/EPCIS_COMPLIANCE.md" className="text-[#bd902e] hover:underline" target="_blank" rel="noreferrer">
                            EPCIS compliance doc
                        </a>
                        ). For every direction you need credentials — remote partners use their host; hosted partners
                        pick up from Antar SFTP.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => setTab('RECEIVE')} className={tabClass('RECEIVE')}>
                            RECEIVE
                        </button>
                        <button type="button" onClick={() => setTab('SEND')} className={tabClass('SEND')}>
                            SEND
                        </button>
                        <button type="button" onClick={() => setTab('BIDIRECTIONAL')} className={tabClass('BIDIRECTIONAL')}>
                            BIDIRECTIONAL
                        </button>
                    </div>
                    {tab === 'RECEIVE' && (
                        <div className="space-y-2 text-xs">
                            <p>
                                <strong>What happens:</strong> Antar polls the partner SFTP server and downloads EPCIS
                                files from the poll path.
                            </p>
                            <p>
                                <strong>Required:</strong> Host, port, username, credentials, <strong>remote poll
                                path</strong>, linked <strong>supplier</strong>.
                            </p>
                            <p>
                                <strong>After setup:</strong> Use <strong>Download files</strong> or wait for the poll
                                schedule (default every 5 minutes).
                            </p>
                        </div>
                    )}
                    {tab === 'SEND' && (
                        <div className="space-y-2 text-xs">
                            <p>
                                <strong>What happens:</strong> Antar uploads EPCIS to the partner&apos;s SFTP server at
                                the upload path.
                            </p>
                            <p>
                                <strong>Required:</strong> Partner&apos;s <strong>host, port, username, credentials</strong>{' '}
                                (same as receive), <strong>remote upload path</strong>, linked <strong>pharmacy</strong>.
                            </p>
                            <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-900">
                                SEND does not mean Antar runs an SFTP server — you still enter the partner&apos;s server
                                details.
                            </p>
                            <p>
                                <strong>After setup:</strong> Use <strong>Send test file</strong> or ship orders with
                                auto-send enabled.
                            </p>
                        </div>
                    )}
                    {tab === 'BIDIRECTIONAL' && (
                        <div className="space-y-2 text-xs">
                            <p>
                                <strong>What happens:</strong> Poll + upload on the same partner connection.
                            </p>
                            <p>
                                <strong>Required:</strong> Host, port, username, credentials, <strong>both</strong> poll
                                and upload paths, linked <strong>supplier</strong> and <strong>pharmacy</strong>.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SftpSetupGuide;
